import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const HeartFailure = () => {
  const symptoms = [
    { icon: 'ri-lungs-line', title: '息切れ', desc: '階段や坂道で息が切れやすくなる' },
    { icon: 'ri-footprint-line', title: 'むくみ', desc: '足や足首がむくむ、靴がきつくなる' },
    { icon: 'ri-moon-line', title: '夜間頻尿', desc: '夜中にトイレに起きる回数が増える' },
    { icon: 'ri-battery-low-line', title: '疲労感', desc: '以前より疲れやすく、だるさを感じる' },
    { icon: 'ri-heart-line', title: '動悸', desc: '心臓がドキドキする、脈が乱れる' },
    { icon: 'ri-weight-line', title: '体重増加', desc: '短期間で体重が2〜3kg増える' },
  ];

  const causes = [
    { title: '高血圧', desc: '長期間の高血圧により心臓に負担がかかる', color: 'bg-red-100 text-red-600' },
    { title: '虚血性心疾患', desc: '心筋梗塞や狭心症による心筋のダメージ', color: 'bg-orange-100 text-orange-600' },
    { title: '弁膜症', desc: '心臓の弁の機能不全による血流障害', color: 'bg-yellow-100 text-yellow-600' },
    { title: '心筋症', desc: '心筋自体の病気による機能低下', color: 'bg-green-100 text-green-600' },
    { title: '不整脈', desc: '心房細動などによる心機能の低下', color: 'bg-teal-100 text-teal-600' },
  ];

  const exacerbationFactors = [
    { icon: 'ri-drop-line', title: '腎機能悪化', desc: '体内の水分・塩分調節が困難に' },
    { icon: 'ri-heart-pulse-line', title: '貧血', desc: '酸素運搬能力の低下' },
    { icon: 'ri-restaurant-line', title: '塩分過剰摂取', desc: '体内の水分貯留を促進' },
    { icon: 'ri-cup-line', title: '水分過剰摂取', desc: '心臓への負担増加' },
    { icon: 'ri-virus-line', title: '感染症', desc: '心臓への追加的ストレス' },
    { icon: 'ri-moon-foggy-line', title: '不眠', desc: '心身の回復力低下' },
  ];

  const essential8 = [
    { icon: 'ri-heart-pulse-line', title: '血圧管理', color: 'bg-red-100 text-red-600' },
    { icon: 'ri-test-tube-line', title: '血糖管理', color: 'bg-orange-100 text-orange-600' },
    { icon: 'ri-drop-line', title: '脂質管理', color: 'bg-yellow-100 text-yellow-600' },
    { icon: 'ri-smoke-line', title: '禁煙', color: 'bg-green-100 text-green-600' },
    { icon: 'ri-scales-3-line', title: '適正体重', color: 'bg-teal-100 text-teal-600' },
    { icon: 'ri-run-line', title: '運動', color: 'bg-blue-100 text-blue-600' },
    { icon: 'ri-restaurant-2-line', title: '栄養', color: 'bg-purple-100 text-purple-600' },
    { icon: 'ri-zzz-line', title: '睡眠', color: 'bg-indigo-100 text-indigo-600' },
  ];

  const additionalFactors = [
    { icon: 'ri-tooth-line', title: 'オーラルケア', color: 'bg-pink-100 text-pink-600' },
    { icon: 'ri-mental-health-line', title: 'メンタルケア', color: 'bg-cyan-100 text-cyan-600' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-24">
        <section className="py-20 bg-gradient-to-br from-primary-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              心不全とは
            </h1>
            <p className="text-lg text-gray-700 max-w-3xl">
              心不全の基礎知識と症状、原因について正しく理解しましょう
            </p>
          </div>
        </section>

        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">心不全とは何か</h2>
                <div className="space-y-6">
                  <p className="text-gray-700 leading-relaxed">
                    心不全とは、心臓のポンプ機能が低下し、全身に十分な血液を送り出せなくなった状態を指します。心臓は全身に酸素や栄養を運ぶ血液を送り出すポンプの役割を果たしていますが、この機能が弱まることで、様々な症状が現れます。
                  </p>
                  <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-xl">
                    <p className="text-gray-900 font-bold mb-2">重要：心不全は病態であり病名ではありません</p>
                    <p className="text-gray-700 leading-relaxed">
                      心不全は一つの病気ではなく、様々な心臓病の終末像として現れる症候群です。そのため、心不全かどうかを考える場合、<strong>基礎心疾患として何があるのかを精査する必要</strong>があります。また、<strong>今回心不全が増悪した契機が何であるかを調べる必要</strong>があります。
                    </p>
                  </div>
                  <div className="bg-primary-50 border-l-4 border-primary-500 p-6 rounded-r-xl">
                    <p className="text-gray-900 font-bold mb-2">治療の重要なポイント</p>
                    <p className="text-gray-700 leading-relaxed">
                      心不全は適切な治療により、症状をコントロールし、進行を遅らせることができます。早期発見と継続的な管理が非常に重要です。
                    </p>
                  </div>
                </div>
              </div>
              <div className="w-full h-96 rounded-3xl overflow-hidden shadow-xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/ad1f33189f33f1c03678cb7bf8424951.jpg"
                  alt="心不全の病態イメージ"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="mb-20">
              <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">心不全の主な症状</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {symptoms.map((symptom, index) => (
                  <div
                    key={index}
                    className="bg-white border-2 border-gray-200 p-8 rounded-2xl hover:border-primary-500 hover:shadow-lg transition-all group cursor-pointer"
                  >
                    <div className="w-14 h-14 flex items-center justify-center bg-primary-100 rounded-xl mb-6 group-hover:bg-primary-500 transition-all">
                      <i className={`${symptom.icon} text-3xl text-primary-600 group-hover:text-white transition-all`}></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{symptom.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{symptom.desc}</p>
                  </div>
                ))}
              </div>
              <div className="mt-8 text-center">
                <p className="text-gray-600">
                  これらの症状が複数当てはまる場合は、早めに医療機関を受診することをお勧めします
                </p>
              </div>
            </div>

            <div className="mb-20">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">基礎心疾患と増悪因子の特定が重要</h2>
              <p className="text-gray-700 text-center mb-12 max-w-4xl mx-auto leading-relaxed">
                これら基礎疾患と増悪させる原因を特定すること、つまり<strong>病因を明らかにすることが治療に重要</strong>となります。心臓の原疾患の治療を行うとともに、増悪させる原因を日常生活から取り除くことが重要になります。
              </p>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <span className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-lg">
                      <i className="ri-heart-line text-xl text-red-600"></i>
                    </span>
                    基礎心疾患
                  </h3>
                  <p className="text-gray-600 mb-6 text-sm">心不全の主な原因となる心臓の病気</p>
                  <div className="grid grid-cols-1 gap-4">
                    {causes.map((cause, index) => (
                      <div
                        key={index}
                        className="bg-white border-2 border-gray-200 p-6 rounded-xl hover:shadow-lg transition-all cursor-pointer"
                      >
                        <div className={`inline-block px-4 py-2 ${cause.color} rounded-full font-bold mb-3 text-sm`}>
                          {cause.title}
                        </div>
                        <p className="text-gray-600 leading-relaxed text-sm">{cause.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <span className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-lg">
                      <i className="ri-alert-line text-xl text-orange-600"></i>
                    </span>
                    増悪させる原因
                  </h3>
                  <p className="text-gray-600 mb-6 text-sm">心不全を悪化させる要因</p>
                  <div className="grid grid-cols-1 gap-4">
                    {exacerbationFactors.map((factor, index) => (
                      <div
                        key={index}
                        className="bg-white border-2 border-gray-200 p-6 rounded-xl hover:border-orange-500 hover:shadow-lg transition-all group cursor-pointer"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0 group-hover:bg-orange-500 transition-all">
                            <i className={`${factor.icon} text-2xl text-orange-600 group-hover:text-white transition-all`}></i>
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 mb-2">{factor.title}</h4>
                            <p className="text-gray-600 text-sm leading-relaxed">{factor.desc}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-20 bg-gradient-to-br from-teal-50 to-blue-50 rounded-3xl p-12">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">修正可能なリスク因子の管理</h2>
                <p className="text-gray-700 leading-relaxed max-w-4xl mx-auto">
                  アメリカ心臓病学会では<strong>「Life's Essential 8」</strong>として、8つの修正可能な因子の評価と管理を提唱しています。当院の心臓リハビリチームでは、それら8個に加えて、<strong>オーラルケア、メンタルケアを合わせた10個の修正可能な因子の管理</strong>を目的としています。
                </p>
              </div>

              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">Life's Essential 8</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {essential8.map((item, index) => (
                    <div
                      key={index}
                      className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all cursor-pointer"
                    >
                      <div className={`w-14 h-14 flex items-center justify-center ${item.color} rounded-xl mb-4 mx-auto`}>
                        <i className={`${item.icon} text-2xl`}></i>
                      </div>
                      <p className="text-center font-bold text-gray-900 text-sm">{item.title}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-center gap-4 mb-8">
                <div className="h-px bg-gray-300 flex-1"></div>
                <span className="text-gray-600 font-medium">+</span>
                <div className="h-px bg-gray-300 flex-1"></div>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">当院独自の追加管理項目</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                  {additionalFactors.map((item, index) => (
                    <div
                      key={index}
                      className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all cursor-pointer"
                    >
                      <div className={`w-14 h-14 flex items-center justify-center ${item.color} rounded-xl mb-4 mx-auto`}>
                        <i className={`${item.icon} text-2xl`}></i>
                      </div>
                      <p className="text-center font-bold text-gray-900">{item.title}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-12 bg-white rounded-2xl p-8 border-2 border-teal-200">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-xl flex-shrink-0">
                    <i className="ri-lightbulb-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2 text-lg">包括的な管理アプローチ</h4>
                    <p className="text-gray-700 leading-relaxed">
                      これら10個の因子を総合的に管理することで、心不全の予防と進行抑制を目指します。一つひとつの因子を改善することが、心臓の健康維持につながります。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default HeartFailure;
